import type {
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    InitializeExtArgs,
    CreateExtArgs
} from '@wix/document-manager-core'
import type {DistributorExtensionAPI} from '../distributor/distributor'
import type {EventHandlerExtApi, Handler} from './eventHandlerExtension'
import {aiFilesUploadServerlessUrl, aiFileUploadServerlessUrl} from './constants'
import {postForMedia} from './utils'

export interface FileUploadApi {
    uploadFile(handler: Handler, url: string, timeOutInMS?: number): Promise<boolean>
    uploadFiles(handlers: Handler[], urls: string[], timeOutInMS?: number): Promise<boolean>
}

export type FileUploadExtApi = ExtensionAPI & {fileUpload: FileUploadApi}

const fileUploadTimeout = 30000

const createExtension = ({environmentContext}: CreateExtensionArgument): Extension => {
    const initialize = async (extArgs: InitializeExtArgs) => {
        const {extensionAPI} = extArgs
        const {distributor} = extensionAPI as DistributorExtensionAPI

        // Register to distributor event for file uploads
        distributor.subscribeToMessage('FILE_UPLOAD', (data: any) =>
            (extensionAPI as EventHandlerExtApi).eventHandler.invokeExecutionHandler(data)
        )
    }

    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): FileUploadExtApi => {
        const {serverFacade} = environmentContext
        const eventHandlerApi = (extensionAPI as EventHandlerExtApi).eventHandler

        const fetchFile = async (url: string): Promise<string> => {
            const result = await postForMedia<string>(serverFacade, aiFileUploadServerlessUrl, {url})
            return result
        }

        const fetchFiles = async (urls: string[]): Promise<string[]> => {
            const result = await postForMedia<string[]>(serverFacade, aiFilesUploadServerlessUrl, {urls})
            return result
        }

        const uploadFile = async (
            handler: Handler,
            url: string,
            timeOutInMS: number = fileUploadTimeout
        ): Promise<boolean> => {
            const executionId = await fetchFile(url)
            eventHandlerApi.registerExecutionIdToHandler(executionId, handler, timeOutInMS)
            return true
        }

        const uploadFiles = async (
            handlers: Handler[],
            urls: string[],
            timeOutInMS: number = fileUploadTimeout
        ): Promise<boolean> => {
            const executionIds = await fetchFiles(urls)
            executionIds.forEach((executionId, index) => {
                eventHandlerApi.registerExecutionIdToHandler(executionId, handlers[index], timeOutInMS)
            })
            return true
        }

        return {
            fileUpload: {
                uploadFile,
                uploadFiles
            }
        }
    }

    return {
        name: 'fileUpload',
        createExtensionAPI,
        initialize
    }
}

export {createExtension}
